import { Eye, EyeClosed } from "@phosphor-icons/react";
import { Button, Grid, Text, TextField } from "@radix-ui/themes";
/* eslint-disable-next-line import/named */
import { Link as RouterLink, useSearch } from "@tanstack/react-router";
import cx from "classnames";
import { useReducer, type FormEventHandler } from "react";
import { useTranslation } from "react-i18next";

import { forgotPasswordRoute } from "../forgot-password/route";

interface LoginFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  isValid: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  checkValidity: () => void;
  loading: boolean;
  disabled: boolean;
  error: boolean;
  errorMessage?: string;
  "data-testid"?: string;
}

export function LoginForm({
  formRef,
  isValid,
  onSubmit,
  checkValidity,
  loading,
  disabled,
  error,
  errorMessage,
  "data-testid": dataTestId = "login-form",
}: LoginFormProps) {
  const [isPasswordVisible, togglePasswordVisibility] = useReducer(
    (x) => !x,
    false,
  );
  const search = useSearch({ strict: false });
  const { t } = useTranslation("auth");

  return (
    <>
      <Text
        weight="medium"
        className="mb-2 hidden text-2xl tracking-tight sm:block"
        data-testid="login-description"
      >
        {t("login.heading")}
      </Text>

      <form
        onSubmit={onSubmit}
        className="grid gap-4 sm:gap-6"
        ref={formRef}
        data-testid={dataTestId}
        aria-label={t("login.formAriaLabel")}
      >
        <Grid gap={{ initial: "1", sm: "3" }}>
          <Text weight="medium" data-testid="email-field-label">
            {t("login.workEmailLabel")}
          </Text>
          <TextField.Root
            required
            variant="classic"
            name="email"
            id="email-input"
            placeholder={t("login.workEmailPlaceholder")}
            type="email"
            autoComplete="true"
            disabled={disabled}
            onInput={checkValidity}
            autoFocus
            size="2"
            data-testid="email-field"
          />
          <Text
            color="red"
            size="2"
            data-testid="email-error-message"
            className={cx({ hidden: !error })}
          >
            {t("login.emailErrorMessage")}
          </Text>
        </Grid>

        <Grid gap="2">
          <Grid gap={{ initial: "1", sm: "3" }}>
            <Text weight="medium" data-testid="password-field-label">
              {t("login.passwordLabel")}
            </Text>
            <TextField.Root
              required
              variant="classic"
              onInput={checkValidity}
              name="password"
              id="password-input"
              placeholder={t("login.passwordPlaceholder")}
              type={isPasswordVisible ? "text" : "password"}
              autoComplete="true"
              disabled={disabled}
              data-testid="password-field"
            >
              <TextField.Slot
                side="right"
                className="px-3"
                onClick={togglePasswordVisibility}
              >
                {isPasswordVisible ? (
                  <EyeClosed size={16} />
                ) : (
                  <Eye size={16} />
                )}
              </TextField.Slot>
            </TextField.Root>
            <Text
              color="red"
              size="2"
              data-testid="password-error-message"
              className={cx({ hidden: !error })}
            >
              {errorMessage || t("login.passwordErrorMessage")}
            </Text>
          </Grid>

          <RouterLink
            to={forgotPasswordRoute.to}
            search={search.redirect ? { redirect: search.redirect } : undefined}
            data-testid="forgot-password-link"
            className="puntt-link w-max underline decoration-puntt-accent-5 underline-offset-4"
          >
            <Text size="2">{t("login.forgotPassword")}</Text>
          </RouterLink>
        </Grid>

        <Button
          type="submit"
          disabled={!isValid || disabled}
          loading={loading}
          className="mt-2 w-full py-3 font-medium"
          data-testid="form-submit-cta"
        >
          {t("login.submitButton")}
        </Button>
      </form>
    </>
  );
}
